import React, {useContext, useState} from "react";
import {AppContext} from "../../../App";
import Stepper from "./Stepper";
import {postRequest} from "../../../helpers/APIHelper";
import Loading from "../../Loading";
import DefaultHelmet from "../../DefaultHelmet";
import Validator from "../../Form/Validator";
import SellFormStep from "./SellFormStep";
import {RoutingHelper} from "../../Navigation/Routing";
import {useSearchParams} from "react-router-dom";
import SellFormIntro from "./SellFormIntro";

export const sellFormDefault = {
    type: "",
    livingArea: 50,
    terrainArea: 400,
    constructionYear: 2000,
    rooms: 2,
    state: "",
    bathrooms: 1,
    garage: false,
    parkingSpot: false,
    additionalCharacteristics: "",
    city: "",
    postalCode: "",
    address: "",
    title: "",
    lastName: "",
    name: "",
    phone: "",
    email: "",
    motive: "",
    rgpd: false,
};

export const fieldData = {
    type: {
        type: "select", required: true, default: sellFormDefault.type, groups: true,
        title: "Quel type de projet immobilier vous concerne ?",
        options: [{
            title: "Appartement", options: [
                {title: "Appartement", value: "Appartement"},
                {title: "Dernier étage", value: "Dernier étage"},
                {title: "Duplex", value: "Duplex"},
                {title: "Appartement mansardé", value: "Appartement mansardé"},
                {title: "Appartement en terrasse", value: "Appartement en terrasse"},
                {title: "Studio", value: "Studio"}
            ]
        },
            {
                title: "Maison", options: [
                    {title: "Maison individuelle", value: "Maison individuelle"},
                    {title: "Maison jumelée", value: "Maison jumelée"},
                    {title: "Maison - mitoyenne en angle", value: "Maison - mitoyenne en angle"},
                    {title: "Maison - mitoyenne", value: "Maison - mitoyenne"}
                ]
            }]
    },
    livingArea: {
        type: "slider",
        required: true,
        default: sellFormDefault.livingArea,
        title: "Surface habitable (m²)",
        min: 20,
        max: 800,
        step: 1
    },
    terrainArea: {
        type: "slider",
        required: true,
        default: sellFormDefault.terrainArea,
        title: "Surface terrain (m²)",
        min: 50,
        max: 5000,
        step: 1
    },
    constructionYear: {
        type: "slider",
        required: true,
        default: sellFormDefault.constructionYear,
        title: "Année de construction",
        min: 1850,
        max: 2026,
        step: 1
    },
    rooms: {
        type: "slider",
        required: true,
        default: sellFormDefault.rooms,
        title: "Nombre de pièces",
        min: 1,
        max: 20,
        step: 0.5
    },
    state: {
        type: "select", required: true, default: sellFormDefault.state, title: "Quel est l'état actuel de votre bien ?",
        options: [
            {title: "À rénover", value: "À rénover"},
            {title: "Bon état", value: "Bon état"},
            {title: "Rénové", value: "Rénové"}
        ]
    },
    bathrooms: {
        type: "slider",
        required: true,
        default: sellFormDefault.bathrooms,
        title: "Nombre de salles de bain",
        min: 1,
        max: 5,
        step: 1
    },
    garage: {
        type: "toggle", required: true, default: sellFormDefault.garage,
        title: "Votre bien a-t-il un place de garage ?", values: ["Oui", "Non"]
    },
    parkingSpot: {
        type: "toggle",
        required: true,
        default: sellFormDefault.parkingSpot,
        title: "Votre bien a-t-il une place de parking ?",
        values: ["Oui", "Non"]
    },
    additionalCharacteristics: {
        type: "textarea",
        default: sellFormDefault.additionalCharacteristics,
        title: "Autres caractéristiques"
    },
    city: {type: "text", required: true, default: sellFormDefault.city, title: "Ville"},
    postalCode: {type: "number", required: true, default: sellFormDefault.postalCode, title: "Code postal"},
    address: {type: "text", required: false, default: sellFormDefault.address, title: "Adresse"},
    title: {type: "preferredTitleSelect", required: true, default: sellFormDefault.title, title: "Titre"},
    lastName: {type: "text", required: true, default: sellFormDefault.lastName, title: "Nom"},
    name: {type: "text", required: true, default: sellFormDefault.name, title: "Prénom"},
    phone: {type: "phone", required: false, default: sellFormDefault.phone, title: "Téléphone (avec indicatif)"},
    email: {type: "email", required: true, default: sellFormDefault.email, title: "Adresse e-mail"},
    motive: {
        type: "radio", required: true, default: sellFormDefault.motive, title: "Motif de votre demande", options: [
            {label: "Vendre", value: "Vendre"},
            {label: "Par curiosité", value: "Par curiosité"},
            {label: "Autre", value: "Autre"},
        ]
    },
    rgpd: {
        type: "checkbox",
        required: true,
        default: sellFormDefault.rgpd,
    },
};

export const SellFormContext = React.createContext({
    sellForm: sellFormDefault,
    setSellForm: (sellForm) => {
        this.sellForm = sellForm;
    }
});

function SellForm() {
    const {setToastIsOpen, setToastMessage, setToastType} = useContext(AppContext);
    const [searchParams] = useSearchParams();

    const [sellForm, setSellForm] = useState(sellFormDefault);
    const [errors, setErrors] = useState(null);

    function updateSellForm(field, value) {
        let tempSellForm = JSON.parse(JSON.stringify(sellForm));

        tempSellForm[field] = value;

        setSellForm(tempSellForm);
    }

    const value = {sellForm, setSellForm, updateSellForm};

    const [currentStep, setCurrentStep] = useState(searchParams.has("etape") ? parseInt(searchParams.get("etape")) - 1 : 0);

    const [loading, setLoading] = useState(false);

    const steps = [
        {
            title: "Introduction",
            subtitle: "",
            icon: <svg className="w-3 h-3" aria-hidden="true"
                       xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor"
                       viewBox="0 0 24 24">
                <path fillRule="evenodd"
                      d="M8 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1h2a2 2 0 0 1 2 2v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2Zm6 1h-4v2H9a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2h-1V4Zm-6 8a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Zm1 3a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9Z"
                      clipRule="evenodd"/>
            </svg>,
            fields: [],
        },
        {
            title: "Informations et état",
            subtitle: "Décrivez votre bien",
            icon: <svg className="w-3 h-3" aria-hidden="true"
                       xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor"
                       viewBox="0 0 24 24">
                <path d="M12 12c-.5523 0-1 .4477-1 1s.4477 1 1 1 1-.4477 1-1-.4477-1-1-1Z"/>
                <path fillRule="evenodd"
                      d="M6.65111 11.6306 12 5.04736l5.3489 6.58324c.1899.2337.475.3694.7761.3694H21v7c0 .5523-.4477 1-1 1H4c-.55228 0-1-.4477-1-1v-7h2.87499c.30113 0 .58623-.1357.77612-.3694ZM8.99999 13c0-1.6568 1.34311-3 3.00001-3 1.6568 0 3 1.3432 3 3 0 1.6569-1.3432 3-3 3-1.6569 0-3.00001-1.3431-3.00001-3Z"
                      clipRule="evenodd"/>
                <path
                    d="m2.5 10 2.7-3.59999c.18885-.2518.48524-.4.8-.4h2.64902L5.39902 10H2.5Zm12.851-3.99999L18.601 10H21.5l-2.7-3.59999c-.1888-.2518-.4852-.4-.8-.4h-2.649Z"/>
            </svg>,
            fields: ["type", "livingArea", "terrainArea", "rooms", "bathrooms", "constructionYear", "state"]
        },
        {
            title: "Informations complémentaires",
            subtitle: "Caractéristiques de votre bien",
            icon: <svg className="w-3 h-3" aria-hidden="true"
                       xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor"
                       viewBox="0 0 24 24">
                <path fillRule="evenodd"
                      d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.408-5.5a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2h-.01ZM10 10a1 1 0 1 0 0 2h1v3h-1a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-1v-4a1 1 0 0 0-1-1h-2Z"
                      clipRule="evenodd"/>
            </svg>,
            fields: ["garage", "parkingSpot", "additionalCharacteristics"],
        },
        {
            title: "Localisation",
            subtitle: "Où se situe votre bien ?",
            icon: <svg className="w-3 h-3" aria-hidden="true"
                       xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor"
                       viewBox="0 0 24 24">
                <path fillRule="evenodd"
                      d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z"
                      clipRule="evenodd"/>
            </svg>,
            fields: ["city", "postalCode", "address"],
        },
        {
            title: "Votre contact",
            subtitle: "Comment pouvons-nous vous contacter ?",
            icon: <svg className="w-3 h-3" aria-hidden="true"
                       xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 18 20">
                <path
                    d="M16 1h-3.278A1.992 1.992 0 0 0 11 0H7a1.993 1.993 0 0 0-1.722 1H2a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2ZM7 2h4v3H7V2Zm5.7 8.289-3.975 3.857a1 1 0 0 1-1.393 0L5.3 12.182a1.002 1.002 0 1 1 1.4-1.436l1.328 1.289 3.28-3.181a1 1 0 1 1 1.392 1.435Z"/>
            </svg>,
            fields: ["title", "lastName", "name", "phone", "email", "motive", "rgpd"],
        }
    ];

    function handleBack() {
        if (currentStep > 0) {
            window.scrollTo(0, 0);
            setCurrentStep(currentStep - 1);
        }
    }

    async function handleNext() {
        if (validate()) {
            window.scrollTo(0, 0);
            if (currentStep < steps.length - 1) {
                setCurrentStep(currentStep + 1);
                setToastIsOpen(false);
            }
            if (currentStep === steps.length - 1) {
                setLoading(true);
                const {data, error} = await postRequest({endpoint: "/contact/sell", body: sellForm});
                setLoading(false);

                if (data?.data.sent) {
                    window.location.replace(RoutingHelper.getPath('estimation-envoyee') + '?formulaire=envoye');
                } else {
                    setErrors(error);
                }
            }
        } else {
            setToastType("error");
            setToastMessage("Des erreurs sont présentes dans le formulaire.");
            setToastIsOpen(true);
        }
    }

    function validate() {
        let stepFieldData = {};

        for (let field of steps[currentStep].fields) {
            stepFieldData[field] = fieldData[field];
        }

        const validation = Validator.validate(sellForm, stepFieldData);

        setErrors(validation);

        return !validation.hasErrors;
    }

    return (
        <>
            <DefaultHelmet
                title="Faites estimer votre bien immobilier"
                description="Obtenez une estimation immobilière rapide de votre appartement, maison ou commerce avec FOTI Immobilier. Expertise locale et sans engagement."
                og_description="Obtenez une estimation immobilière rapide de votre appartement, maison ou commerce avec FOTI Immobilier. Expertise locale et sans engagement."
            />
            <div className="container mx-auto w-full md:w-4/5 flex flex-col h-full md:pb-24">
                <div className="py-2 px-4 overflow-y-scroll md:overflow-y-auto">
                    <div className="px-4 mb-2 md:mb-8">
                        <Stepper steps={steps} currentStepId={currentStep} setStep={setCurrentStep}/>
                    </div>
                    {currentStep > 0 ?
                        <SellFormContext.Provider value={value}>
                            <SellFormStep title={steps[currentStep].subtitle} errors={errors}
                                          fields={steps[currentStep].fields}/>
                        </SellFormContext.Provider>
                        :
                        <SellFormIntro />
                    }
                </div>
                <div
                    className={"fixed left-0 bottom-0 px-2 md:px-36 p-4 bg-white w-full flex " + (currentStep === 0 ? "justify-center" : "justify-between")}>
                    <button onClick={handleBack}
                            className={"text-primary hover:text-primary-light underline px-2 py-1" + (currentStep === 0 ? " hidden" : "")}>
                        Retour
                    </button>
                    <button onClick={handleNext}
                            className="text-white text-lg md:text-xl bg-primary hover:bg-white hover:text-primary border-2 border-primary px-2 py-1">{currentStep < 4 ? (currentStep === 0 ? "C'est parti!" : "Continuer") : loading ?
                        <Loading page={false}/> : "Envoyer ma demande"}</button>
                </div>
                {currentStep > 0 && (
                    <div className="mt-10 md:mt-24 w-11/12 mx-auto md:w-full">
                        <SellFormIntro/>
                    </div>
                )}
            </div>
        </>
    );
}

export default SellForm;
